import { Suspense } from "react";
import { useGlobalSearchParams } from "expo-router";
import { Card, MatchCase } from "@ptsd/uikit";
import { useTranslation } from "react-i18next";
import { View } from "tamagui";

import type { TSpheres } from "@edenlabllc/ptsd-validators";

import { api } from "~/utils/api";
import {
  ErrorBoundary,
  LoadingScreen,
  PreconditionFallback,
} from "~/components/blocks";
import { LinearChartByScale } from "~/components/blocks/chart";
import {
  GeneralFilter,
  GeneralFilterProvider,
  resolveFilter,
  useGeneralFilter,
} from "~/components/blocks/filters";
import {
  ComposedTextSphere,
  normalizeSphereComposedText,
} from "~/components/feature/composed-text";
import {
  DynamicsTable,
  ImminentRisk,
  MetricsDisclaimer,
  MetricsSidebar,
} from "~/components/feature/metrics";
import type { RecommendationData } from "~/components/feature/metrics/recommendations";
import {
  normalizeRecommendations,
  RecommendationsGrid,
} from "~/components/feature/metrics/recommendations";
import { normalizeScales, ScalesList } from "~/components/feature/scales";
import { MetricsPageWrapper } from "~/components/layout/layout";

const HMT_DOMAIN = "hmt";

const Metrics = () => {
  const slug = useGlobalSearchParams().slug as unknown as TSpheres;

  return (
    <Suspense fallback={<LoadingScreen height={"100dvh"} width={"100%"} />}>
      <Suspense>
        <MetricsSidebar slug={slug} />
      </Suspense>
      <MetricsPageWrapper>
        <GeneralFilterProvider>
          <View gap={"$3"}>
            <MetricsDisclaimer />
            <GeneralFilter withGroupBy />
            <WithEmptyStateFallback slug={slug} />
          </View>
        </GeneralFilterProvider>
      </MetricsPageWrapper>
    </Suspense>
  );
};

export default Metrics;

const WithEmptyStateFallback: React.FC<{ slug: TSpheres }> = ({ slug }) => {
  const { filter } = useGeneralFilter();
  return (
    <MatchCase value={slug}>
      {(Case) => (
        <ErrorBoundary resetKeys={filter} fallback={PreconditionFallback}>
          <Case condition={HMT_DOMAIN}>
            <HmtDashboard />
          </Case>
          <Case condition={(slug) => slug !== HMT_DOMAIN}>
            <Suspense
              fallback={<LoadingScreen height={"100dvh"} width={"100%"} />}
            >
              <SphereDashboard slug={slug} />
            </Suspense>
          </Case>
        </ErrorBoundary>
      )}
    </MatchCase>
  );
};

const HmtDashboard = () => {
  const { t } = useTranslation("metrics");
  return (
    <>
      <Suspense fallback={<LoadingScreen height={590} width={"100%"} />}>
        <LinearChartByScale
          title={t("hmt-dashboard.linear-chart.title")}
          scale={HMT_DOMAIN}
        />
      </Suspense>
      <Suspense fallback={<LoadingScreen height={250} width={"100%"} />}>
        <ImminentRisk />
      </Suspense>
      <Suspense fallback={<LoadingScreen height={500} width={"100%"} />}>
        <DynamicsTable />
      </Suspense>
      <Suspense fallback={<LoadingScreen height={500} width={"100%"} />}>
        <HmtRecommendations />
      </Suspense>
    </>
  );
};

const HmtRecommendations = () => {
  const { t } = useTranslation("metrics");
  const { filter } = useGeneralFilter();
  const { groupBy: _, ...rest } = filter;

  const [recommendations] = api.dashboard.getHmtRecommendation.useSuspenseQuery(
    {
      filter: resolveFilter(rest),
    },
    {
      select: (data) => {
        return normalizeRecommendations(data.data);
      },
    },
  );

  if (recommendations.length === 0) return null;

  return (
    <Card paddingVertical="$5" gap={"$5"} shadow="md">
      <Card.Title data-id="hmt-recommendations-title">
        {t("hmt-dashboard.recommendations.title")}
      </Card.Title>
      <RecommendationsGrid data={recommendations} />
    </Card>
  );
};

const SphereDashboard: React.FC<{ slug: TSpheres }> = ({ slug }) => {
  const { filter } = useGeneralFilter();
  const { groupBy: _, ...rest } = filter;

  const [data] = api.dashboard.getSphereInfo.useSuspenseQuery(
    {
      filter: resolveFilter(rest),
      sphere: slug,
    },
    {
      select: (data) => {
        const { recommendations, scales, ...rest } = data;
        return {
          composedText: normalizeSphereComposedText(rest),
          scale: rest.scale,
          title: rest.title,
          recommendations: normalizeRecommendations(recommendations),
          scales: normalizeScales(scales),
        };
      },
    },
  );

  return (
    <>
      <ComposedTextSphere data={data?.composedText} />
      <Suspense fallback={<LoadingScreen height={500} width={"100%"} />}>
        <LinearChartByScale title={data.title} scale={data.scale} />
      </Suspense>
      <ScalesList data={data.scales} />
      <SphereRecommendations data={data?.recommendations} />
    </>
  );
};

const SphereRecommendations: React.FC<{
  data: RecommendationData[];
}> = ({ data }) => {
  const { t } = useTranslation("metrics");
  return (
    <Card paddingVertical="$5" gap={"$5"} shadow="md">
      <Card.Title>{t("spheres-dashboard.recommendations.title")}</Card.Title>
      <RecommendationsGrid data={data} />
    </Card>
  );
};
